/**
 * knowledgeGateVocabulary — the single place the technician-facing Adaptive Knowledge Gate surface
 * spells the gate's contract (MASTER_FLOW §4.1, pilot-critical).
 *
 * Why this file exists rather than labels inline in the panel. §4.1's mismatch path is only real if
 * a technician can put a run on it and take it off again, and the words used to do that are the
 * contract: "vendor_official" is not a synonym for "official-looking", and a trigger the backend
 * cannot name is not a trigger the UI may quietly drop. Every list below mirrors a closed vocabulary
 * in `backend/app/services/adaptive_knowledge_gate.py`, and `knowledgeGateVocabulary.test.js` parses
 * that module and fails if the two ever diverge — so this file can drift only by turning the build
 * red. Pure data and pure functions: no JSX, no data access, no authorization opinion.
 *
 * What this module deliberately does NOT do: decide whether a mismatch may be cleared. That is the
 * backend evaluator's job, and it needs the citation and the server-stamped retrieval time to do it.
 * The UI's only outputs here are what the server already decided and what the server already
 * refused — never a local "unblocked".
 */

// ── The run's gate state, as the technician should read it ──────────────────────────────────────

export const GATE_STATE = {
  LOADING: "loading",
  // The endpoint's 404: nothing was ever observed to contradict the approved Expert Pack. This is
  // §4.1's normal path, and it is NOT the same answer as "the gate permits the mutation" — the
  // executor, not this read, is what permits execution.
  NORMAL_PATH: "normal_path",
  BLOCKED: "blocked",
  CLEARED: "cleared",
  // The gate state could not be read or could not be understood. Must never be rendered as clear:
  // "was a mismatch recorded against this run" is exactly the question that must not be answered by
  // a guess, which is also why manager_agent refuses the action in this state.
  UNKNOWN: "unknown",
};

export const GATE_CONTRACT_VERSION_SEEN = "1.0";

export const GATE_DECISIONS = {
  PROCEED: "proceed",
  RESEARCH_REQUIRED: "research_required",
  STOP_ESCALATE: "stop_escalate",
};

// ── Triggers: §4.1's list, complete and closed ──────────────────────────────────────────────────

export const GATE_TRIGGERS = [
  {
    id: "unknown_or_changed_api_field_or_response",
    label: "שדה או תגובת API לא מוכרים / השתנו",
    explain:
      "המערכת קיבלה מהמערכת החיצונית שדה, שם או מבנה תגובה שלא הופיעו בחבילת המומחה המאושרת. אין לנחש מה המשמעות — יש לאמת מול התיעוד הרשמי.",
  },
  {
    id: "ui_mismatch",
    label: "אי-התאמה במסך או בבקרה",
    explain:
      "המסך או הבקרה בפועל אינם תואמים למה שחבילת המומחה מתארת (כפתור חסר, תווית שונה, מסך אחר). פעולה על בסיס מיקום או תווית שהונחו עלולה לפגוע במסך הלא נכון.",
  },
  {
    id: "deprecation_notice",
    label: "הודעת הפסקת תמיכה",
    explain:
      "הספק הודיע שהממשק, הפרמטר או היכולת שבשימוש יופסקו. ייתכן שהפעולה עדיין עובדת היום אך אינה דרך הפעולה הנכונה.",
  },
  {
    id: "unexpected_permission_behaviour",
    label: "התנהגות הרשאות לא צפויה",
    explain:
      "ההרשאות בפועל אינן כפי שהחבילה מצפה — פעולה שהצליחה אמורה להיכשל, או להיפך. אין להסיק הרשאה מכך שקריאה הצליחה.",
  },
  {
    id: "product_or_version_mismatch",
    label: "אי-התאמה במוצר או בגרסה",
    explain:
      "המוצר או הגרסה בפועל שונים מאלה שהחבילה נכתבה עבורם. אותה פעולה יכולה להיות שונה לחלוטין בין גרסאות.",
  },
  {
    id: "contradictory_evidence",
    label: "ראיות סותרות",
    explain: "שני מקורות או שתי קריאות חוזרות אומרים דברים שונים. סתירה אינה נפתרת בבחירת הצד הנוח.",
  },
  {
    id: "confidence_below_threshold",
    label: "רמת ביטחון מתחת לסף",
    explain:
      "הביטחון במסקנה נמוך מהסף המוגדר. ביטחון נמוך הוא עצמו סיבה לעצור ולאמת, לא סיבה לנסח ביתר זהירות.",
  },
];

export const GATE_TRIGGER_IDS = GATE_TRIGGERS.map((t) => t.id);

const TRIGGER_BY_ID = new Map(GATE_TRIGGERS.map((t) => [t.id, t]));

export const gateTriggerMeta = (id) =>
  TRIGGER_BY_ID.get(id) || {
    id,
    // A trigger row the server recorded but this build cannot name is surfaced verbatim rather than
    // dropped: dropping it would make a blocked run look like it had fewer reasons than it does.
    label: id,
    explain: "טריגר שנרשם בשרת ואינו מוכר לגרסת הממשק הזו — מוצג כפי שנרשם.",
    unknown: true,
  };

// ── Where research came from ────────────────────────────────────────────────────────────────────

const AUTHORITY_CAN_CLEAR_NOTE = {
  vendor_official:
    "המקור היחיד שיכול להסיר אי-התאמה: תיעוד רשמי ועדכני של הספק, עם ציטוט ומועד בדיקה.",
  community: "עשוי לעזור לאבחן, אך אינו מחייב ולא יכול להסיר אי-התאמה בפעולה שמשנה מצב בייצור.",
  unclassified: "מקור שלא סווג. אינו יכול להסיר אי-התאמה.",
};

export const RESEARCH_AUTHORITIES = [
  { id: "vendor_official", label: "תיעוד רשמי של הספק", canClear: true, explain: AUTHORITY_CAN_CLEAR_NOTE.vendor_official },
  { id: "community", label: "חומר קהילתי", canClear: false, explain: AUTHORITY_CAN_CLEAR_NOTE.community },
  { id: "unclassified", label: "לא מסווג", canClear: false, explain: AUTHORITY_CAN_CLEAR_NOTE.unclassified },
];

export const RESEARCH_AUTHORITY_IDS = RESEARCH_AUTHORITIES.map((a) => a.id);

const AUTHORITY_BY_ID = new Map(RESEARCH_AUTHORITIES.map((a) => [a.id, a]));

export const researchAuthorityMeta = (id) =>
  AUTHORITY_BY_ID.get(id) || {
    id,
    label: id || "לא דווח",
    canClear: false,
    explain: "מקור שאינו מוכר לגרסת הממשק הזו — אינו יכול להסיר אי-התאמה.",
    unknown: true,
  };

// ── Why the gate decided what it decided ────────────────────────────────────────────────────────

export const GATE_REASON_LABELS = {
  no_mismatch_recorded: "לא נרשמה אי-התאמה",
  targeted_research_not_yet_performed: "טרם בוצע מחקר ממוקד במקור הרשמי",
  research_source_is_not_vendor_official: "המקור שנרשם אינו רשמי של הספק",
  research_evidence_missing_a_citation_or_timestamp: "בראיות חסר ציטוט או מועד בדיקה",
  research_evidence_does_not_reconcile_the_mismatch: "הראיות אינן מיישבות את אי-ההתאמה שנצפתה",
  mismatch_reconciled_by_current_vendor_evidence: "אי-ההתאמה יושבה בראיות ספק עדכניות",
  unrecognized_trigger_cannot_be_evaluated: "טריגר שאינו מוכר — לא ניתן להעריך אותו",
};

export const gateReasonLabel = (code) => GATE_REASON_LABELS[code] || code || "לא דווח";

// The observation-context keys the backend persists. A fixed allowlist there, mirrored here so the
// form cannot offer a field that would be silently dropped — or worse, invite someone to paste a
// vendor response body, which is exactly what the allowlist exists to prevent.
export const OBSERVATION_CONTEXT_FIELDS = [
  { key: "domain", label: "תחום", placeholder: "m365 / eset / acronis" },
  { key: "product", label: "מוצר", placeholder: "Microsoft 365" },
  { key: "version", label: "גרסה", placeholder: "כפי שנצפה בפועל" },
  { key: "endpoint", label: "נקודת קצה", placeholder: "GET /users/{id}" },
  { key: "ui_screen", label: "מסך / בקרה", placeholder: "מסך ההרשאות" },
];

export const OBSERVATION_CONTEXT_KEYS = OBSERVATION_CONTEXT_FIELDS.map((f) => f.key);

// ── What the decision rested on (MASTER_FLOW §4.1, backend FULL-0008) ───────────────────────────

/**
 * The basis a gate decision was evaluated against, as the server reports it: which persisted Expert
 * Pack revision (its scope, its revision number within that scope, and the content hash that names
 * it) and which vendor interface declaration revision the response was judged against.
 *
 * §4.1 requires the exact knowledge version a historical run used to stay recoverable. The backend
 * has persisted and returned this since FULL-0008 (`knowledge_gate_decisions.pack_scope_key` /
 * `pack_revision_seq` / `pack_content_hash` / `contract_id` / `contract_revision`, projected by
 * `GateDecisionOut`); this is the reader that was missing.
 *
 * The pack half is all-or-nothing, mirroring `adaptive_knowledge_gate.latest_gate_decision`'s own
 * condition rather than restating a new rule: a partial triple is not a name for a revision, and
 * half of an identity rendered as an identity is worse than none. Each half is `null` when the
 * server did not name it, and null means **not recorded** — never "there was none". A decision
 * recorded before the basis was stored, and a mismatch a technician typed with no vendor response in
 * hand, both carry none, and the panel says so rather than leaving the row blank.
 */
export function gateDecisionBasis(data) {
  const pack = data?.pack_revision;
  const packRecorded =
    !!pack &&
    typeof pack === "object" &&
    typeof pack.scope_key === "string" &&
    pack.scope_key !== "" &&
    Number.isInteger(pack.revision_seq) &&
    typeof pack.content_hash === "string" &&
    pack.content_hash !== "";
  const contractId = typeof data?.contract_id === "string" && data.contract_id ? data.contract_id : null;
  const contractRevision =
    typeof data?.contract_revision === "string" && data.contract_revision ? data.contract_revision : null;
  return {
    pack: packRecorded
      ? { scopeKey: pack.scope_key, revisionSeq: pack.revision_seq, contentHash: pack.content_hash }
      : null,
    contractId,
    contractRevision,
  };
}

// ── The run's whole trail, not only its newest decision (§4.1, backend FULL-0042) ───────────────

/**
 * Every decision the server recorded for the run, oldest first, as the server ordered them.
 *
 * `gateDecisionBasis` above reads *one* decision's basis. This reads the trail, which exists because
 * §4.1 requires the earlier decision a later lookup reconciled to stay readable, and
 * `GET /runs/{run_id}` answers only the newest row. Until this reader existed, the reconciled
 * `research_required` was stored, named in the audit trail, and on no screen.
 *
 * Three rules, each of which a test pins:
 *   - **the server's order is the order.** Entries are returned as received and are never re-sorted
 *     locally: the server orders by the run's stored insertion order, which is the only well-defined
 *     "when" for two decisions recorded in the same tick — the wall clock cannot separate them, and
 *     the row's random UUID would order them arbitrarily.
 *   - **a position the server did not send is null, never an index.** An invented position would read
 *     as the order the gate itself recorded, which is the one thing this block must not fabricate.
 *   - **the basis is read by the same function the newest decision's basis is.** A trail row and the
 *     state row are the same row rendered twice, so they must not be able to disagree about it.
 *
 * A row that is not an object is dropped rather than rendered as a blank decision; a row that is an
 * object but carries no decision string is kept, because "the server recorded something here and this
 * build cannot read it" is a fact about the record, not an absence of one.
 */
export function gateDecisionTrail(data) {
  const rows = Array.isArray(data?.decisions) ? data.decisions : [];
  return rows
    .filter((row) => row && typeof row === "object")
    .map((row) => {
      const triggers = Array.isArray(row.triggers)
        ? row.triggers.filter((t) => typeof t === "string")
        : [];
      return {
        seq: Number.isInteger(row.seq) ? row.seq : null,
        // A free-text timestamp the server stamped. Displayed, never parsed into an order.
        recordedAt: typeof row.recorded_at === "string" && row.recorded_at ? row.recorded_at : null,
        decision: typeof row.decision === "string" && row.decision ? row.decision : null,
        reasonCode: typeof row.reason_code === "string" && row.reason_code ? row.reason_code : null,
        triggers,
        unknownTriggers: triggers.filter((t) => !TRIGGER_BY_ID.has(t)),
        observedContext:
          row.observed_context && typeof row.observed_context === "object" ? row.observed_context : {},
        evidenceRef: typeof row.evidence_ref === "string" && row.evidence_ref ? row.evidence_ref : null,
        researchAuthority:
          typeof row.research_authority === "string" && row.research_authority
            ? row.research_authority
            : null,
        basis: gateDecisionBasis(row),
      };
    });
}

export const GATE_TRAIL_STATUS = {
  LOADING: "loading",
  READY: "ready",
  // The trail could not be read. Deliberately its own status rather than READY with no entries: the
  // whole reason this block exists is to stop the newest decision reading as the entire record, and
  // rendering an unreadable trail as an empty one would restore exactly that, one level down.
  UNAVAILABLE: "unavailable",
};

/** The trail query as the panel renders it: a status, and the entries when there are any to show. */
export function classifyGateTrailQuery(query) {
  if (!query) return { status: GATE_TRAIL_STATUS.LOADING, entries: [] };
  if (query.isError) return { status: GATE_TRAIL_STATUS.UNAVAILABLE, entries: [] };
  if (query.data == null || !query.dataUpdatedAt) {
    return { status: GATE_TRAIL_STATUS.LOADING, entries: [] };
  }
  return { status: GATE_TRAIL_STATUS.READY, entries: gateDecisionTrail(query.data) };
}

// ── Reading the server's answer ─────────────────────────────────────────────────────────────────

/**
 * The projection the panel renders, from a server `GateDecisionOut`. Fail-closed by construction: a
 * payload without a boolean `permits_mutation` classifies as UNKNOWN, never as CLEARED.
 */
export function classifyGatePayload(data) {
  if (!data || typeof data !== "object") {
    return { state: GATE_STATE.UNKNOWN, decision: null, reasonCode: null, triggers: [], unknownTriggers: [], observedContext: {}, evidenceRef: null, researchAuthority: null, contractVersion: null, basis: gateDecisionBasis(null) };
  }
  const triggers = Array.isArray(data.triggers) ? data.triggers.filter((t) => typeof t === "string") : [];
  const unknownTriggers = triggers.filter((t) => !TRIGGER_BY_ID.has(t));
  const base = {
    decision: typeof data.decision === "string" ? data.decision : null,
    reasonCode: typeof data.reason_code === "string" ? data.reason_code : null,
    triggers,
    unknownTriggers,
    observedContext: data.observed_context && typeof data.observed_context === "object" ? data.observed_context : {},
    evidenceRef: typeof data.evidence_ref === "string" && data.evidence_ref ? data.evidence_ref : null,
    researchAuthority: typeof data.research_authority === "string" && data.research_authority ? data.research_authority : null,
    contractVersion: typeof data.contract_version === "string" ? data.contract_version : null,
    // Descriptive only. The basis says what the decision was evaluated *against*; it is never an
    // input to whether the gate blocks, which stays `permits_mutation` alone (see the state lines
    // below). A payload carrying a basis answers no more of the blocking question than one without.
    basis: gateDecisionBasis(data),
  };
  if (typeof data.permits_mutation !== "boolean") {
    // A payload that does not answer the one question that matters is not evidence that the answer
    // is "yes". The panel renders this as unverifiable and the executor refuses on the same basis.
    return { ...base, state: GATE_STATE.UNKNOWN, malformed: true };
  }
  return {
    ...base,
    state: data.permits_mutation ? GATE_STATE.CLEARED : GATE_STATE.BLOCKED,
    malformed: false,
  };
}

/**
 * Classify a react-query result into the panel's state. `stale` is reported separately rather than
 * as a fifth state: a refetch that failed after an earlier successful read still has a real answer,
 * but the surface must say the answer is old instead of presenting it as current.
 */
export function classifyGateQuery(query) {
  if (!query) return { state: GATE_STATE.LOADING, stale: false };
  const status = query?.error?.response?.status;

  if (query.isError) {
    // Checked before the retained-payload branch on purpose. Rows are append-only, so a 404 after a
    // 200 is not "the earlier block still stands" — it is the server's current answer that nothing
    // is recorded, and rendering the retained block instead would be inventing a state.
    if (status === 404) {
      // The panel is mounted for a run the caller already loaded, so the endpoint's other 404s
      // (run/ticket/agent row missing) are not what this means here.
      return { state: GATE_STATE.NORMAL_PATH, stale: false };
    }
    if (query.data != null && query.dataUpdatedAt > 0) {
      return { ...classifyGatePayload(query.data), stale: true };
    }
    return {
      state: GATE_STATE.UNKNOWN,
      stale: false,
      errorDetail: query?.error?.response?.data?.detail || null,
      errorStatus: typeof status === "number" ? status : null,
    };
  }

  if (query.data == null || !query.dataUpdatedAt) return { state: GATE_STATE.LOADING, stale: false };
  return { ...classifyGatePayload(query.data), stale: false };
}

/** True when the gate is standing between an already-approved action and its execution. */
export const gateIsBlocking = (classification) => classification?.state === GATE_STATE.BLOCKED;

/**
 * What the technician can do next, given the gate state and what they are allowed to do. This is a
 * statement about the *surface*, not about authority: `canObserve`/`canResearch` are the caller's
 * existing agent permissions, never granted here.
 */
export function gateNextStep(classification, { canObserve = false, canResearch = false } = {}) {
  switch (classification?.state) {
    case GATE_STATE.BLOCKED:
      if (canResearch) {
        return "record_research";
      }
      // The research write re-permits an action a human already approved, so the server requires
      // agents_approve for it. A technician without it is told who to hand this to, and is never
      // shown a control that would fail.
      return canObserve ? "escalate_to_approver" : "read_only";
    case GATE_STATE.NORMAL_PATH:
    case GATE_STATE.CLEARED:
      return canObserve ? "can_report_mismatch" : "read_only";
    default:
      return "read_only";
  }
}
